import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Palette, Printer, BookOpen, Package, Globe, Gift, Image,
  ArrowRight, ChevronRight, MapPin,
  CheckCircle, Target, Eye, Heart,
  MessageSquare, Layers, Truck, Star,
} from "lucide-react";
import "./Aboutus.css";

// ─── Data ─────────────────────────────────────────────────────
// Real local images — same pool as Services.jsx / ServiceDetails.jsx
import threeD1    from "../assets/images/threeD2.jpeg";
import threeD2    from "../assets/images/threeD.jpg";
import brand1     from "../assets/images/brand1.jpeg";
import brand2     from "../assets/images/brand2.jpeg";
import brochure   from "../assets/images/brochure.jpg";
import book1      from "../assets/images/book1.jpeg";
import bags1      from "../assets/images/bags1.jpg";
import tshirt     from "../assets/images/tshirt.jpg";
// ─── useInView ────────────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}


const STORY_IMGS = [
  { src: threeD1,  alt: "3D Signage Installation",  cls: "ab-sc--tall" },
  { src: brand1,   alt: "Brand Identity Work"                           },
  { src: brochure, alt: "Print Production"                              },
  { src: book1,    alt: "Book Printing",             cls: "ab-sc--wide" },
];

const VALUES = [
  { label: "Excellence",            desc: "Uncompromising quality in every project we touch."    },
  { label: "Creativity",            desc: "Fresh ideas that make brands unforgettable."           },
  { label: "Innovation",            desc: "Forward-thinking solutions for a changing world."      },
  { label: "Integrity",             desc: "Honest, transparent partnerships built on trust."      },
  { label: "Customer Satisfaction", desc: "Your success is the only measure that matters."       },
  { label: "Reliability",           desc: "On time, every time — no excuses."                   },
];

// Maps to ServiceDetails.jsx route ids
const SERVICES_DATA = [
  { Icon: Box,      label: "3D Signage",          desc: "Shop fronts, illuminated letters, reception signs and wayfinding.",   id: "3d-signage"    },
  { Icon: Palette,  label: "Brand Identity",      desc: "Logos, brand guidelines, stationery and social media kits.",          id: "branding"      },
  { Icon: Printer,  label: "Printing Services",   desc: "Business cards, flyers, brochures, posters and catalogues.",          id: "printing"      },
  { Icon: BookOpen, label: "Book Printing",       desc: "Professional printing, binding and publishing solutions.",            id: "book-printing" },
  { Icon: Package,  label: "Packaging Solutions", desc: "Paper bags, gift boxes, product packaging and custom labels.",        id: "packaging"     },
  { Icon: Globe,    label: "Web Design & Dev",    desc: "Fast, modern websites built to convert visitors into customers.",     id: "web-design"    },
  { Icon: Gift,     label: "Promotional Merch",   desc: "Branded mugs, t-shirts, caps and corporate gift sets.",              id: "merchandise"   },
  { Icon: Image,    label: "Large Format Print",  desc: "Banners, rollups, vehicle wraps and window graphics.",               id: "large-format"  },
];

const STATS = [
  { n: "100+", l: "Projects Completed" },
  { n: "98%",  l: "Client Satisfaction" },
  { n: "24hr", l: "Response Time"       },
  { n: "5+",   l: "Years Experience"   },
];

const WHY_ITEMS = [
  "Professional Quality",
  "Creative Solutions",
  "Fast Turnaround",
  "Affordable Pricing",
  "Dedicated Support",
  "End-to-End Service",
];

const PROCESS_STEPS = [
  { n: "01", Icon: MessageSquare, title: "Discovery",   desc: "We listen to understand your goals, audience and market before anything else.", red: false },
  { n: "02", Icon: Palette,       title: "Design",      desc: "Creative concepts crafted for your brief, reviewed until they're unmistakably right.", red: false },
  { n: "03", Icon: Layers,        title: "Production",  desc: "Industry-grade production with rigorous quality checks at every stage.", red: false },
  { n: "04", Icon: Truck,         title: "Delivery",    desc: "On-time delivery and professional installation, anywhere across Kenya.", red: true },
];

// ─── Shared UI ────────────────────────────────────────────────
function Pill({ children }) {
  return <div className="ab-pill"><span className="ab-dot" />{children}</div>;
}
function GD() { return <div className="ab-gd" />; }

// ─── HERO ─────────────────────────────────────────────────────
function Hero() {
  const [ref, vis] = useInView(0.05);
  const navigate   = useNavigate();
  return (
    <section ref={ref} className="ab-hero" aria-label="About hero">
      <div className="ab-hero__grid"  aria-hidden="true" />
      <div className="ab-hero__sq"    aria-hidden="true" />
      <div className="ab-hero__dot"   aria-hidden="true" />
      <div className="ab-hero__sm"    aria-hidden="true" />

      <div className="ab-hero__inner">
        <div className={`ab-fade${vis ? " in" : ""}`}>
          <Pill>About Bestmark Designers</Pill>
        </div>

        <h1 className={`ab-hero__h1 ab-fade d1${vis ? " in" : ""}`}>
          Building Brands That{" "}
          <em className="ab-italic">Get Noticed</em>
        </h1>

        <p className={`ab-hero__sub ab-fade d2${vis ? " in" : ""}`}>
          At Bestmark Designers, we combine creativity, innovation and craftsmanship
          to help businesses stand out through exceptional branding, printing, signage,
          packaging, book printing, and digital solutions.
        </p>

        <div className={`ab-hero__btns ab-fade d3${vis ? " in" : ""}`}>
          <button className="ab-btn-r ab-btn--lg" onClick={() => navigate("/portfolio")}>
            View Our Work <ArrowRight size={15} />
          </button>
          <button className="ab-btn-g ab-btn--lg" onClick={() => navigate("/contact")}>
            Get Free Quote <ChevronRight size={15} />
          </button>
        </div>

        <div className={`ab-hero__stats ab-fade d4${vis ? " in" : ""}`}>
          {[
            ["100+", "Projects Done"],
            ["5+",   "Years in Kenya"],
            ["98%",  "Happy Clients"],
            ["8",    "Service Areas"],
          ].map(([n, l]) => (
            <div key={l} className="ab-hero__stat">
              <span className="ab-hero__stat-n">{n}</span>
              <span className="ab-hero__stat-l">{l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── OUR STORY ────────────────────────────────────────────────
function OurStory() {
  const [ref, vis] = useInView(0.08);
  const navigate   = useNavigate();
  return (
    <section className="ab-sec ab-sec--surface" ref={ref} aria-label="Our story">
      <div className="ab-inner">
        <div className="ab-story">
          {/* LEFT — image collage */}
          <div className={`ab-story__imgs ab-left${vis ? " in" : ""}`}>
            <div className="ab-collage">
              {STORY_IMGS.map((img, i) => (
                <div key={i} className={`ab-collage__item${img.cls ? " " + img.cls : ""}`}>
                  <img src={img.src} alt={img.alt} loading={i === 0 ? "eager" : "lazy"} />
                  <div className="ab-collage__tint" />
                </div>
              ))}
            </div>

            {/* Floating badge over collage */}
            <div className="ab-story__badge">
              <div className="ab-story__badge-n">5+</div>
              <div className="ab-story__badge-l">Years of Excellence</div>
            </div>
          </div>

          {/* RIGHT — text */}
          <div className={`ab-story__text ab-right${vis ? " in" : ""}`}>
            <div style={{ marginBottom: 14 }}><Pill>Our Story</Pill></div>

            <h2 className="ab-h2">
              From a Printing Desk to a{" "}
              <em className="ab-italic">Full-Service Studio</em>
            </h2>

            <div className="ab-story__paras">
              <p className="ab-story__lead">
                Bestmark Designers was founded with one mission: to help businesses
                create memorable brands that leave lasting impressions.
              </p>
              <p className="ab-story__body">
                What started as a creative design and printing service has grown into
                a full-service branding agency — signage, printing, packaging,
                promotional merchandise, book printing, and web design, all under
                one roof.
              </p>
              <p className="ab-story__body">
                Today we proudly work with startups, SMEs, schools, churches, NGOs
                and corporates across Kenya.
              </p>
            </div>

            {/* Mini stats row */}
            <div className="ab-story__stats">
              {[["100+","Projects"], ["50+","Clients"], ["8","Services"]].map(([n,l]) => (
                <div key={l} className="ab-story__stat">
                  <span className="ab-story__stat-n">{n}</span>
                  <span className="ab-story__stat-l">{l}</span>
                </div>
              ))}
            </div>

            <div className="ab-story__btns">
              <button className="ab-btn-r" onClick={() => navigate("/services")}>
                Our Services <ArrowRight size={14} />
              </button>
              <button className="ab-btn-g" onClick={() => navigate("/contact")}>
                Get a Quote <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── MISSION / VISION / VALUES ────────────────────────────────
function MissionVision() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="ab-sec" ref={ref} aria-label="Mission, vision and values">
      <div className="ab-inner">
        <div className="ab-sec__head ab-sec__head--center">
          <div className={`ab-fade${vis ? " in" : ""}`}><Pill>What Drives Us</Pill></div>
          <h2 className={`ab-h2 ab-fade d1${vis ? " in" : ""}`}>Mission, Vision & Values</h2>
          <p className={`ab-sub ab-fade d2${vis ? " in" : ""}`}>
            Every decision we make is guided by a clear purpose and values we refuse to compromise on.
          </p>
        </div>

        <div className="ab-mvv">

          {/* ── Row 1 ────────────────────────────────────────── */}

          {/* Mission — wide accent hero card */}
          <div className={`ab-card ab-mvv__mission ab-scl d1${vis ? " in" : ""}`}>
            <div className="ab-mvv__mission-orb" />
            <div className="ab-mvv__mission-glow" />
            <div className="ab-mvv__mission-inner">
              <div className="ab-mvv__icon-wrap">
                <Target size={20} color="#E31E24" strokeWidth={1.6} />
              </div>
              <div className="ab-mvv__label">Mission</div>
              <h3 className="ab-mvv__mission-h">Our Purpose</h3>
              <p className="ab-mvv__mission-body">
                To deliver creative branding and printing solutions that help businesses
                communicate, grow and succeed across Kenya and beyond.
              </p>
            </div>
            {/* Large ghost text */}
            <div className="ab-mvv__ghost" aria-hidden="true">Mission</div>
          </div>

          {/* Stat — 98% */}
          <div className={`ab-card ab-mvv__stat-card ab-scl d2${vis ? " in" : ""}`}>
            <div className="ab-mvv__stat-orb" />
            <Star size={20} color="#E31E24" strokeWidth={1.5} style={{ position:"relative",zIndex:1 }} />
            <div style={{ position:"relative",zIndex:1 }}>
              <div className="ab-mvv__big-n">98<span>%</span></div>
              <div className="ab-mvv__big-l">Client Satisfaction</div>
            </div>
          </div>

          {/* Stat — 100+ */}
          <div className={`ab-card ab-mvv__stat-card ab-scl d3${vis ? " in" : ""}`}>
            <div className="ab-mvv__stat-orb" />
            <CheckCircle size={20} color="#E31E24" strokeWidth={1.5} style={{ position:"relative",zIndex:1 }} />
            <div style={{ position:"relative",zIndex:1 }}>
              <div className="ab-mvv__big-n">100<span>+</span></div>
              <div className="ab-mvv__big-l">Projects Completed</div>
            </div>
          </div>

          {/* ── Row 2 ────────────────────────────────────────── */}

          {/* Values — tall card, left */}
          <div className={`ab-card ab-mvv__values ab-scl d2${vis ? " in" : ""}`}>
            <div className="ab-mvv__values-orb" />
            <div style={{ position:"relative",zIndex:1 }}>
              <div className="ab-mvv__icon-wrap" style={{ marginBottom:12 }}>
                <Heart size={18} color="#E31E24" strokeWidth={1.6} />
              </div>
              <div className="ab-mvv__label">Core Values</div>
              <h3 className="ab-mvv__section-h">What We<br />Stand By</h3>
            </div>
            <ul className="ab-values" style={{ position:"relative",zIndex:1 }}>
              {VALUES.map(v => (
                <li key={v.label} className="ab-values__item">
                  <div className="ab-values__check">
                    <CheckCircle size={11} color="#E31E24" />
                  </div>
                  <div>
                    <span className="ab-values__name">{v.label}</span>
                    <span className="ab-values__desc"> — {v.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Vision — tall card, right */}
          <div className={`ab-card ab-mvv__vision ab-scl d3${vis ? " in" : ""}`}>
            <div className="ab-mvv__vision-orb" />
            <div className="ab-mvv__vision-inner">
              <div>
                <div className="ab-mvv__icon-wrap" style={{ marginBottom:12 }}>
                  <Eye size={18} color="#E31E24" strokeWidth={1.6} />
                </div>
                <div className="ab-mvv__label">Vision</div>
                <h3 className="ab-mvv__section-h">Where<br />We're Going</h3>
                <p className="ab-mvv__body">
                  To become Africa's most trusted branding, printing and creative solutions
                  company — known for quality, innovation and results.
                </p>
              </div>
              {/* Africa badge */}
              <div className="ab-mvv__africa-badge">
                <MapPin size={14} color="#E31E24" style={{ flexShrink:0 }} />
                <span>Kenya & beyond</span>
              </div>
            </div>
            {/* Big ghost number */}
            <div className="ab-mvv__ghost ab-mvv__ghost--bottom" aria-hidden="true">2030</div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── WHAT WE DO ───────────────────────────────────────────────
function WhatWeDo() {
  const [ref, vis] = useInView(0.06);
  const navigate   = useNavigate();
  return (
    <section className="ab-sec ab-sec--surface" ref={ref} aria-label="Services">
      <div className="ab-inner">
        <div className="ab-sec__head">
          <div>
            <div className={`ab-fade${vis ? " in" : ""}`}><Pill>What We Do</Pill></div>
            <h2 className={`ab-h2 ab-fade d1${vis ? " in" : ""}`}>
              Eight Services,<br />One Roof
            </h2>
            <p className={`ab-sub ab-fade d2${vis ? " in" : ""}`}>
              Eight disciplines, one studio — a complete creative partner for your brand.
            </p>
          </div>
          <div className={`ab-fade d3${vis ? " in" : ""}`}>
            <button className="ab-btn-r" onClick={() => navigate("/services")}>
              All Services <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <div className="ab-svc-grid">
          {SERVICES_DATA.map(({ Icon, label, desc, id }, i) => (
            <div
              key={label}
              className={`ab-card ab-svc__card ab-scl d${Math.min(i % 4 + 1, 6)}${vis ? " in" : ""}`}
              onClick={() => { navigate(`/services/${id}`); window.scrollTo({ top:0, behavior:"smooth" }); }}
              role="button"
              tabIndex={0}
              aria-label={`Go to ${label} service`}
              onKeyDown={e => { if (e.key === "Enter") { navigate(`/services/${id}`); window.scrollTo({ top:0, behavior:"smooth" }); } }}
            >
              <div className="ab-svc__orb" />
              <div className="ab-svc__inner">
                <div className="ab-ibox"><Icon size={18} color="#E31E24" strokeWidth={1.6} /></div>
                <div>
                  <div className="ab-svc__cat">Service</div>
                  <div className="ab-svc__name">{label}</div>
                  <p className="ab-svc__desc">{desc}</p>
                </div>
              </div>
              <button
                className="ab-btn-g ab-svc__btn"
                onClick={e => { e.stopPropagation(); navigate(`/services/${id}`); window.scrollTo({ top:0, behavior:"smooth" }); }}
              >
                Learn More <ChevronRight size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── WHY CHOOSE US ────────────────────────────────────────────
function WhyUs() {
  const [ref, vis] = useInView(0.08);
  const navigate   = useNavigate();
  return (
    <section className="ab-sec" ref={ref} aria-label="Why choose us">
      <div className="ab-inner">
        <div className="ab-sec__head ab-sec__head--center">
          <div className={`ab-fade${vis ? " in" : ""}`}><Pill>Why Bestmark</Pill></div>
          <h2 className={`ab-h2 ab-fade d1${vis ? " in" : ""}`}>Why Clients Choose Us</h2>
          <p className={`ab-sub ab-fade d2${vis ? " in" : ""}`}>The numbers tell one story. The reasons tell another.</p>
        </div>

        <div className="ab-why">
          {/* Stat cards */}
          {STATS.map((s, i) => (
            <div key={s.n} className={`ab-card ab-why__stat ab-scl d${i + 1}${vis ? " in" : ""}`}>
              <CheckCircle size={20} color="#E31E24" strokeWidth={1.5} />
              <div>
                <div className="ab-why__stat-n">{s.n}</div>
                <div className="ab-why__stat-l">{s.l}</div>
              </div>
            </div>
          ))}

          {/* Nationwide */}
          <div className={`ab-card ab-why__stat ab-why__stat--accent ab-scl d5${vis ? " in" : ""}`}>
            <MapPin size={20} color="#E31E24" strokeWidth={1.5} />
            <div>
              <div className="ab-why__stat-n">KE</div>
              <div className="ab-why__stat-l">Nationwide Kenya</div>
            </div>
          </div>

          {/* Feature card */}
          <div className={`ab-card ab-why__feature ab-scl d6${vis ? " in" : ""}`}>
            <div className="ab-why__feature-orb" />
            <div className="ab-why__feature-inner">
              <div style={{ marginBottom:14 }}><Pill>Why Choose Bestmark</Pill></div>
              <h3 className="ab-why__feature-h">
                A studio that cares as much<br />about your result as you do.
              </h3>
              <div className="ab-why__checklist">
                {WHY_ITEMS.map(item => (
                  <div key={item} className="ab-why__check-item">
                    <div className="ab-why__check-box">
                      <CheckCircle size={11} color="#E31E24" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <button
                className="ab-btn-r ab-why__cta"
                onClick={() => navigate("/contact")}
              >
                Start a Project <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── PROCESS ──────────────────────────────────────────────────
function OurProcess() {
  const [ref, vis] = useInView(0.08);
  const navigate   = useNavigate();
  return (
    <section className="ab-sec ab-sec--surface" ref={ref} aria-label="Our process">
      <div className="ab-inner">
        <div className="ab-sec__head ab-sec__head--center">
          <div className={`ab-fade${vis ? " in" : ""}`}><Pill>How We Work</Pill></div>
          <h2 className={`ab-h2 ab-fade d1${vis ? " in" : ""}`}>From Brief to Brand</h2>
          <p className={`ab-sub ab-fade d2${vis ? " in" : ""}`}>
            Four clear steps. Zero guesswork. A result you'll be proud of.
          </p>
        </div>

        <div className="ab-proc">
          {PROCESS_STEPS.map((step, i) => (
            <div
              key={step.n}
              className={`ab-proc__card ab-scl d${i + 1}${vis ? " in" : ""}${step.red ? " ab-proc__card--red" : ""}`}
            >
              <div className="ab-proc__orb" />
              <div className="ab-proc__top">
                <span className="ab-proc__num">{step.n}</span>
                <div className="ab-ibox ab-ibox--sm">
                  <step.Icon size={15} color="#E31E24" strokeWidth={1.6} />
                </div>
              </div>
              <div className="ab-proc__title">{step.title}</div>
              <p className="ab-proc__desc">{step.desc}</p>
              {step.red && (
                <button
                  className="ab-btn-r ab-proc__btn"
                  onClick={() => navigate("/contact")}
                >
                  Get Started <ArrowRight size={12} />
                </button>
              )}
              {i < PROCESS_STEPS.length - 1 && (
                <div className="ab-proc__arrow" aria-hidden="true" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────
export default function Aboutus() {
  return (
    <>
      <a href="#about-main" className="ab-skip">Skip to main content</a>
      <main id="about-main">
        <Hero />
        <GD />
        <OurStory />
        <GD />
        <MissionVision />
        <GD />
        <WhatWeDo />
        <GD />
        <WhyUs />
        <GD />
        <OurProcess />
      </main>
    </>
  );
}